import { router } from "expo-router";
import React from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Text } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { contentMaxWidths } from "@/src/theme/layout";

const sections = [
  {
    title: "Informations que Neqraw utilise",
    paragraphs: [
      "À la création du compte, Firebase Authentication gère les informations de connexion, notamment ton adresse email et l’identifiant technique de ton compte.",
      "Neqraw enregistre les informations de profil que tu fournis, par exemple ton nom affiché, ton nom d’utilisateur, ton type de compte, ta filière ou matière, et ta photo. Une identité Personnelle et une identité Institution sont distinctes, même lorsqu’elles sont contrôlées par le même compte de connexion.",
      "Neqraw traite aussi les contenus que tu ajoutes au service : publications, commentaires, réactions, adhésions et contributions aux groupes, ainsi que les messages directs ou de groupe. Les photos et documents PDF que tu joins sont stockés avec les contenus auxquels ils se rapportent.",
    ],
  },
  {
    title: "Visibilité des contenus",
    paragraphs: [
      "Les informations de profil public, les publications et les contributions sont présentées aux autres utilisateurs de Neqraw selon le type de contenu, sa visibilité et les règles d’accès du groupe. Les messages sont accessibles aux participants de la conversation. Une personne qui reçoit un message ou une publication partagée peut en conserver l’historique dans sa propre conversation.",
      "Neqraw utilise l’identité Personnelle ou Institution sélectionnée pour attribuer une publication ou un message. L’identifiant Firebase qui contrôle une identité n’est pas, à lui seul, l’identité publique affichée.",
    ],
  },
  {
    title: "Notifications et sécurité",
    paragraphs: [
      "Si les notifications sont activées, Neqraw conserve les jetons push associés aux appareils et utilise le service Expo Push pour acheminer les notifications. Les notifications reçues dans l’application sont également enregistrées dans les données du compte.",
      "Sur Android, Firebase App Check et Google Play Integrity vérifient que les requêtes viennent d’une version reconnue de Neqraw et d’un appareil qui satisfait les contrôles d’intégrité configurés. Le contrôle transmet au backend des éléments techniques comme le nom du package, le numéro de version, l’empreinte du certificat et les résultats d’intégrité. Le backend échange le jeton auprès de Google Play Integrity et conserve des valeurs hachées pour limiter les défis et requêtes répétés.",
    ],
  },
  {
    title: "Stockage et traitement",
    paragraphs: [
      "Neqraw utilise Firebase Authentication pour la connexion, Cloud Firestore pour les profils et les données de l’application, Firebase Storage pour les photos et documents, et Firebase Functions pour certaines opérations sécurisées, notifications et vérifications. Google Play Integrity est utilisé pour les contrôles Android décrits ci-dessus; Expo Push est utilisé pour la livraison des notifications push.",
      "Ces services traitent les données nécessaires au fonctionnement, à la sécurité et à la livraison des fonctionnalités que tu utilises. Cette politique ne fixe pas de durée de conservation uniforme pour chaque catégorie de données.",
    ],
  },
  {
    title: "Suppression d’un compte",
    paragraphs: [
      "Tu peux lancer la suppression depuis Paramètres dans l’application ou depuis la page publique de suppression après t’être connecté et avoir confirmé ton mot de passe. La suppression retire le compte Firebase, l’identité Personnelle, son profil et ses réservations de nom d’utilisateur, les publications et commentaires personnels, les jetons de notification et les relations personnelles prises en charge.",
      "Les messages déjà remis aux autres participants peuvent rester dans leur historique pour préserver leurs conversations; l’identité de l’expéditeur y est remplacée par « Compte supprimé ». Les contenus partagés déjà copiés dans une conversation peuvent également y rester. Les dossiers de signalement, de modération et de sécurité peuvent être conservés lorsqu’ils sont nécessaires à l’intégrité et à la sécurité du service.",
      "Neqraw ne supprime pas automatiquement une identité Institution ou un groupe dont tu es le seul responsable. Dans ce cas, l’opération est bloquée avant toute suppression afin de ne pas orpheliner ces ressources. Les identités Institution qui ont un autre propriétaire actif restent en place et ton accès personnel à celles-ci est retiré.",
    ],
  },
];

export default function PrivacyPolicyScreen() {
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: surface.secondary }}>
      <ScrollView
        contentContainerStyle={{
          alignSelf: "center",
          maxWidth: contentMaxWidths.settings,
          width: "100%",
          paddingHorizontal: 20,
          paddingVertical: 28,
          gap: 18,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
      >
        <View style={{ gap: 8 }}>
          <Text style={{ color: uiText.primary, fontSize: 30, fontWeight: "900" }}>Politique de confidentialité</Text>
          <Text style={{ color: uiText.muted, lineHeight: 20 }}>Neqraw · Mise à jour le 29 septembre 2026</Text>
          <Text style={{ color: uiText.secondary, lineHeight: 22 }}>
            Cette politique explique les données utilisées par Neqraw, une plateforme académique destinée aux étudiants, enseignants et institutions.
          </Text>
        </View>

        {sections.map((section) => (
          <View
            key={section.title}
            style={{
              backgroundColor: surface.primary,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: border.subtle,
              padding: 18,
              gap: 10,
            }}
          >
            <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>{section.title}</Text>
            {section.paragraphs.map((paragraph, index) => (
              <Text key={index} style={{ color: uiText.secondary, lineHeight: 22 }}>
                {paragraph}
              </Text>
            ))}
          </View>
        ))}

        <Pressable
          accessibilityRole="link"
          onPress={() => router.push("/delete-account")}
          style={({ pressed }) => ({
            minHeight: 48,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 12,
            borderWidth: 1,
            borderColor: brand.primary,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text style={{ color: brand.primary, fontWeight: "900" }}>Consulter la procédure de suppression</Text>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
